import { KnowledgeBaseLink } from '@ardoq/knowledge-base';
import { withBasePath } from 'basePath';
import { ArdoqId } from '@ardoq/api-types';
import { DEFAULT_SOLUTION_METADATA, getSolutionMetadata } from '@ardoq/core';
import { reports$ } from 'streams/reports/reports$';
import {
  CreateReportFromLocation,
  OpenReportBuilderFromLocation,
  trackCreateReport,
  trackOpenReportBuilder,
} from '@ardoq/report-reader';
import { dispatchAction } from '@ardoq/rxbeach';
import {
  openProductboardPortal,
  requestShowAppModule,
} from 'appContainer/actions';
import { maybeShowConfirmCloseScenario } from 'appContainer/maybeShowConfirmCloseScenario';
import { AppModules } from 'appContainer/types';
import { openSelectWorkspaceDialog } from 'components/selectWorkspaceDialog/actions';
import { copyWsToOrgModal } from 'copyWsToOrg/copyWsToOrgModal';
import { openNewTab } from 'integrations/actions';
import { isScenarioMode } from 'models/utils/scenarioUtils';
import {
  navigateToAnalyticsOverview,
  navigateToPresentationAdmin,
  navigateToReportBuilder,
  NavigateToReportBuilderPayload,
  NavigateToReportModulePayload,
  navigateToReportOverview,
  navigateToReportReader,
} from 'router/navigationActions';
import { closeScenario } from 'scope/actions';
import { showSupport } from 'utils/support';
import { trackEvent } from '../../tracking/tracking';
import { orgAdminsListCommands } from 'admin/orgAdminsList/commands';
import { navigate } from '../../router/declarative/streamRouterAdapter/actions';
import { Trackable } from 'tracking/trackingData';

export const openHome = (): void => {
  dispatchAction(navigate({ route: '/home', params: {} }));
};

export const openSelectWorkspaceDialogAction = async () => {
  if (isScenarioMode()) {
    const didConfirm = await maybeShowConfirmCloseScenario();
    if (!didConfirm) return;
    dispatchAction(closeScenario());
    dispatchAction(navigate({ route: '/home', params: {} }));
  }
  dispatchAction(openSelectWorkspaceDialog());
};

export const openIntegrationPage = () => {
  dispatchAction(navigate({ route: '/integrations', params: {} }));
};

/** LEGACY CODE */
export const editTemplates = async () => {
  const { editTemplates: editTemplatesModal } =
    await import('./editTemplates/editTemplates');
  await editTemplatesModal();
};

export const openCopyWorkspacesModal = () => {
  copyWsToOrgModal();
};

export const openAdvancedSearch = () => {
  dispatchAction(navigate({ route: '/search/advanced-search', params: {} }));
};

export const openGremlinSearch = () => {
  dispatchAction(navigate({ route: '/search/gremlin', params: {} }));
};

export const openGraphlakeSearch = () => {
  dispatchAction(navigate({ route: '/search/graphlake', params: {} }));
};

export const openPresentations = () => {
  trackEvent('Sidebar: open presentations');
  dispatchAction(navigateToPresentationAdmin());
};

export const openSolutions = (hasContentAwareBundleCopy: boolean) => {
  dispatchAction(
    navigate(
      hasContentAwareBundleCopy
        ? { route: '/solutions', params: {} }
        : { route: '/use-cases', params: {} }
    )
  );
};

export const openDiscover = (orgHasDiscoverInCore: boolean) => {
  if (orgHasDiscoverInCore) {
    dispatchAction(
      navigate({
        route: '/discover',
        params: {},
      })
    );
    return;
  }
  window.open(withBasePath('/discover'), '_blank');
};

export const openMyTasks = () => {
  trackEvent('Sidebar: open tasks');
  dispatchAction(
    navigate({
      route: '/tasks',
      params: {},
    })
  );
};

export const openChatWithUs = ({
  prePopulateMessage,
}: { prePopulateMessage?: string } = {}) =>
  showSupport({ chat: true, prePopulateMessage });

export const openHelp = () => {
  const newTab = window.open(KnowledgeBaseLink.HELP_CENTER, '_blank');
  newTab?.focus();
};

export const openVideoTutorials = () => {
  const newTab = window.open(
    'https://www.youtube.com/playlist?list=PLN64rGvDdWmmQ2HnycLpjT8FE7tzSqp1F',
    '_blank'
  );
  newTab?.focus();
};

export const openUserForum = () => {
  const newTab = window.open('https://community.ardoq.com/', '_blank');
  newTab?.focus();
};

export const openArdoqApi = () => {
  dispatchAction(
    navigate({
      route: '/integrations/:integrationId',
      params: { integrationId: 'public-api' },
    })
  );
};

export const openArdoqDeveloperPortal = () => {
  dispatchAction(openNewTab({ url: 'http://developer.ardoq.com/' }));
};

export const logout = () => {
  if (window?.Intercom) {
    window.Intercom('shutdown');
  }
  trackEvent('Topbar: clicked log out');
  window.location.href = '/logout';
};

export const openManageUsers = async () => {
  trackEvent('Sidebar0 Menu item clicked', { label: 'Show admins' });
  orgAdminsListCommands.openDialog();
};

export const openWorkspace = (hasActiveWorkspace: boolean) => {
  if (hasActiveWorkspace) {
    dispatchAction(
      requestShowAppModule({ selectedModule: AppModules.WORKSPACES })
    );
  }
};

export const openMetamodelOverview = () => {
  dispatchAction(
    navigate({
      route: '/metamodel',
      params: {},
    })
  );
};

export const openOrganizationMetamodelTriples = () => {
  dispatchAction(
    navigate({
      route: '/organization-metamodel/constraints',
      params: {},
    })
  );
};

export const openProductPortal = () => {
  dispatchAction(openProductboardPortal());
};

export const openCreateReport = ({
  from,
  navigateToReportBuilderPayload,
  folderId,
}: {
  from: CreateReportFromLocation;
  navigateToReportBuilderPayload?: NavigateToReportBuilderPayload;
  folderId?: ArdoqId;
}) => {
  trackCreateReport(trackEvent, from);
  trackOpenReportBuilder(trackEvent, from, DEFAULT_SOLUTION_METADATA);
  const adHocSearchReport =
    navigateToReportBuilderPayload &&
    'datasource' in navigateToReportBuilderPayload;
  const supportsFolderId = !adHocSearchReport;
  dispatchAction(
    navigateToReportBuilder(
      supportsFolderId
        ? { ...navigateToReportBuilderPayload, folderId }
        : navigateToReportBuilderPayload
    )
  );
};

export const openReportInBuilder = (
  from: OpenReportBuilderFromLocation,
  navigationPayload: Omit<NavigateToReportModulePayload, 'filters'>
) => {
  const { reportId } = navigationPayload;
  // eslint-disable-next-line ardoq-lint/no-direct-stream-state-access -- imperative one-shot click handler; the analytics payload reads the report snapshot at click time
  const report = reportId ? reports$.state.byId[reportId] : undefined;
  trackOpenReportBuilder(
    trackEvent,
    from,
    report ? getSolutionMetadata(report) : DEFAULT_SOLUTION_METADATA,
    reportId
  );
  dispatchAction(navigateToReportBuilder(navigationPayload));
};

export const openReportInReader = (
  navigationPayload: Trackable<NavigateToReportModulePayload>
) => dispatchAction(navigateToReportReader(navigationPayload));

export const openReportOverview = () =>
  dispatchAction(navigateToReportOverview());

export const goToReportAndDashboardOverview = () =>
  dispatchAction(navigateToAnalyticsOverview());

export const openHelpAndSupport = () => {
  trackEvent('Sidebar: open help and support');
  dispatchAction(
    navigate({
      route: '/help-and-support',
      params: {},
    })
  );
};
